import { useGetCanonicalPageContext } from '#pplx/frontend/apps/ask/layout/primitives/CanonicalProviderShared.js';
import { sendNativeEvent } from '#pplx/frontend/apps/ask/native-bridges/nativeBridge.js';
import { useNativeBridgeActions } from '#pplx/frontend/apps/ask/native-bridges/store.js';
import { persistOAuthCompleteResult } from '#pplx/frontend/apps/ask/oauth-redirect/openRedirectUrl.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
const CANONICAL_MWEB_OAUTH_COMPLETION_TIMEOUT_MS = 5 * 60 * 1_000;
export function useShouldUseDirectOAuthUrl() {
    return Boolean(import.meta.env.VITE_IS_COMPUTER_APP_BUILD || import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD);
}
/**
 * Returns the desktop shell opener used to start OAuth flows on Perplexity
 * desktop builds, or `undefined` elsewhere.
 *
 * On desktop builds, app-shell handling of popups and top-level navigations can
 * prevent OAuth from visibly starting. Hand the auth URL to the OS browser via a
 * shell opener instead. On every other platform we return `undefined` so callers
 * keep the standard `window.open` behavior.
 */
export function useExternalOAuthOpener() {
    // The bridge action is typed as required, but a not-yet-hydrated platform
    // bridge can omit it at runtime, so treat it as possibly undefined.
    const { openExternalURL } = useNativeBridgeActions();
    const { device: { isWindowsApp }, } = useWebPlatform();
    const nativeExternalOAuthOpener = useMemo(() => Object.assign(async (url) => {
        if (!openExternalURL)
            throw new Error('Native OAuth opener is unavailable');
        await openExternalURL(url);
    }, { requiresOAuthCompletionSignal: true }), [openExternalURL]);
    if (import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD) {
        return nativeExternalOAuthOpener;
    }
    if (!isWindowsApp)
        return undefined;
    if (!openExternalURL) {
        logger.warn('useExternalOAuthOpener: on Windows desktop app but openExternalURL is unavailable; OAuth will fall back to window.open');
        return undefined;
    }
    return nativeExternalOAuthOpener;
}
/** Adapts the canonical iOS bridge to the connector flow's completion signal. */
export function useCanonicalMwebOAuthOpener() {
    const { client, inApp } = useGetCanonicalPageContext();
    const isSupported = inApp && client === 'ios';
    const pendingTimeout = useRef(undefined);
    const clearPending = useCallback(() => {
        clearTimeout(pendingTimeout.current);
        pendingTimeout.current = undefined;
    }, []);
    useEffect(() => {
        if (!isSupported)
            return;
        const handleComplete = (event) => {
            if (pendingTimeout.current === undefined)
                return;
            clearPending();
            const detail = event instanceof CustomEvent
                ? event.detail
                : undefined;
            persistOAuthCompleteResult({ success: detail?.success === true });
        };
        document.addEventListener('pplx.oauth.complete', handleComplete);
        return () => {
            document.removeEventListener('pplx.oauth.complete', handleComplete);
            clearPending();
        };
    }, [clearPending, isSupported]);
    const opener = useMemo(() => Object.assign((url) => {
        clearPending();
        pendingTimeout.current = setTimeout(clearPending, CANONICAL_MWEB_OAUTH_COMPLETION_TIMEOUT_MS);
        if (sendNativeEvent({ type: 'pplx.oauth', data: { url } }))
            return;
        clearPending();
        throw new Error('Native OAuth bridge unavailable');
    }, { requiresOAuthCompletionSignal: true }), [clearPending]);
    return isSupported ? opener : undefined;
}
//# sourceMappingURL=useExternalOAuthOpener.js.map